class CustomSize extends HTMLElement {
  constructor() {
    super();
    // Bind our event handlers.
    this.handleButtonClick = this.handleButtonClick.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.variantChangeHandler = this.variantChangeHandler.bind(this);
  }

  connectedCallback() {
    const productEl = this.closest('.product');
    if (!productEl) return; // Exit if no product container found

    // Set up initial state: grab product data and option selectors.
    this.data = this.getProductData();
    this.optionSelectors = Array.from(productEl.querySelectorAll('.option-selector')) || [];
    this.variant = this.getSelectedVariant();
    if (this.variant) {
      this.currentVariant = this.variant;
    }

    // Get the inputs and display elements inside this custom component.
    this.widthInput = this.querySelector('input.custom-size__wall-width') || null;
    this.heightInput = this.querySelector('input.custom-size__wall-height') || null;
    this.errorEl = this.querySelector('.product-price-error') || null;
    this.sqMeterDisplay = this.querySelector('.product-price-sq-meter') || null;
    this.computedPriceDisplay = this.querySelector('.product-computed-price') || null;
    this.computedPriceContainer = this.querySelector('.product-computed-price-container') || null;

    // Add event listeners for the plus/minus buttons and input changes.
    this.addEventListener('click', this.handleButtonClick);
    if (this.widthInput) this.widthInput.addEventListener('change', this.handleInputChange);
    if (this.heightInput) this.heightInput.addEventListener('change', this.handleInputChange);

    // Listen for variant change events.
    document.addEventListener('on:variant:change', this.variantChangeHandler);

    // Initial visibility/behavior based on the currently selected variant.
    const variantId = this.currentVariant?.id;
    const isCustomSize = variantId && this.data?.formatted?.[variantId]?.customSize;
    const qtyInputEl = productEl.querySelector('m-quantity-input');

    if (isCustomSize) {
      this.style.display = '';
      this.setInputsDisabled(false);
      this.updateCalculation();
    } else {
      this.style.display = 'none';
      this.setInputsDisabled(true);
      if (qtyInputEl && typeof qtyInputEl.updateMultiplier === 'function') {
        qtyInputEl.updateMultiplier(1);
      }
      if (this.computedPriceContainer) this.computedPriceContainer.style.display = 'none';
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  disconnectedCallback() {
    document.removeEventListener('on:variant:change', this.variantChangeHandler);
  }

  handleButtonClick(evt) {
    if (!evt.target.matches('.custom-size__btn')) return;
    evt.preventDefault();
    const field = evt.target.dataset.field;
    const input = this.querySelector(`input.${field}`);
    if (!input) return;
    if (evt.target.name === 'plus') {
      input.stepUp();
    } else if (evt.target.name === 'minus') {
      input.stepDown();
    }
    input.dispatchEvent(new Event('change'));
  }

  handleInputChange() {
    this.updateCalculation();
  }

  updateCalculation() {
    // Ensure required inputs exist.
    if (!this.widthInput || !this.heightInput) return;
    // Do not calculate while hidden/disabled (non-custom-size variants).
    if (this.widthInput.disabled || this.style.display === 'none') return;

    // Get dimensions and compute area in m².
    const width = parseFloat(this.widthInput.value) || 0;
    const height = parseFloat(this.heightInput.value) || 0;
    const area = (width * height) / 10000;
    if (this.sqMeterDisplay) {
      this.sqMeterDisplay.textContent = area.toFixed(2);
    }

    // Determine if there's an error (area below minimum).
    const minSqMeter = parseFloat(window.theme?.settings?.customSizeMinimumSqMeter) || 2;
    let hasError = false;
    if (area < minSqMeter) {
      hasError = true;
      if (this.errorEl) this.errorEl.hidden = false;
    } else {
      if (this.errorEl) this.errorEl.hidden = true;
    }

    // Compute the unit value and the new Shopify quantity for one roll.
    const multiplierSetting = parseFloat(window.theme?.settings?.customDimensionsUnitMultiplier) || 10;
    const unit = area * multiplierSetting;
    const rollQuantity = 1;
    const rounding = window.theme?.settings?.customDimensionsQuantityRounding;
    const newShopifyQuantity = rounding === 'ceil' ? Math.ceil(unit * rollQuantity) : Math.round(unit * rollQuantity);

    // Save the computed quantity for later use.
    this.newShopifyQuantity = newShopifyQuantity;

    // Only update the m-quantity-input's multiplier if there is no error.
    if (!hasError) {
      const qtyInputEl = this.closest('.product').querySelector('m-quantity-input');
      if (qtyInputEl && typeof qtyInputEl.updateMultiplier === 'function') {
        qtyInputEl.updateMultiplier(newShopifyQuantity);
      }
    }

    // Calculate and update the total price only when there is no error.
    if (!hasError && this.currentVariant) {
      const totalPrice = this.currentVariant.price * newShopifyQuantity;
      if (this.computedPriceDisplay && window.theme?.shopifyFormatCurrency) {
        this.computedPriceDisplay.textContent = window.theme.shopifyFormatCurrency(
          totalPrice,
          window.theme.settings.moneyFormat,
        );
        if (this.computedPriceContainer) {
          this.computedPriceContainer.style.display = ''; // show the price
        }
      }
    } else {
      // Hide the price display if there's an error.
      if (this.computedPriceContainer) {
        this.computedPriceContainer.style.display = 'none';
      }
    }

    // Update the "Add to Cart" button state based on error.
    this.updateAddToCartButton(hasError);
  }

  /**
   * Updates the Add to Cart button.
   * If there is an error, the button is disabled; otherwise, it's enabled.
   */
  updateAddToCartButton(hasError) {
    const productEl = this.closest('.product');
    if (!productEl) return;
    this.addBtn = this.addBtn || productEl.querySelector('[name="add"]');
    if (this.addBtn) {
      this.addBtn.disabled = hasError;
    }
  }

  variantChangeHandler(event) {
    if (!event?.detail?.variant) return;
    const variantId = event.detail.variant.id;
    const customSize = event.detail.variantData?.formatted?.[variantId]?.customSize || false;
    const qtyInputEl = this.closest('.product').querySelector('m-quantity-input');
    if (customSize) {
      // Update the active variant and recalculate.
      this.currentVariant = event.detail.variant;
      this.style.display = '';
      this.setInputsDisabled(false);
      this.updateCalculation();
    } else {
      // Hide the custom-size component if the selected variant is not custom-sized.
      this.style.display = 'none';
      this.setInputsDisabled(true);
      // Reset the multiplier on m-quantity-input to 1.
      if (qtyInputEl && typeof qtyInputEl.updateMultiplier === 'function') {
        qtyInputEl.updateMultiplier(1);
      }
      if (this.computedPriceContainer) this.computedPriceContainer.style.display = 'none';
      if (this.errorEl) this.errorEl.hidden = true;
    }
  }

  // Retrieves product data from the closest product element.
  getProductData() {
    const productEl = this.closest('.product');
    if (!productEl) return null;
    const dataEl = productEl.querySelector('[type="application/json"]');
    return dataEl ? JSON.parse(dataEl.textContent) : null;
  }

  // Retrieves the selected option values from each option selector.
  getSelectedOptions() {
    const selectedOptions = [];
    this.optionSelectors.forEach((selector) => {
      if (!selector) return;
      if (selector.dataset.selectorType === 'dropdown') {
        const btn = selector.querySelector('.custom-select__btn');
        const selectedText = btn ? btn.textContent.trim() : '';
        selectedOptions.push(selectedText === this.dataset.placeholderText ? null : selectedText);
      } else {
        const selected = selector.querySelector('input:checked');
        selectedOptions.push(selected ? selected.value : null);
      }
    });
    return selectedOptions;
  }

  // Finds the selected variant based on the current options.
  getSelectedVariant() {
    const selectedOptions = this.getSelectedOptions();
    return this.data && this.data.product && Array.isArray(this.data.product.variants)
      ? this.data.product.variants.find((v) => v.options.every((val, index) => val === selectedOptions[index]))
      : null;
  }
}
CustomSize.prototype.setInputsDisabled = function (disabled) {
  if (this.widthInput) this.widthInput.disabled = disabled;
  if (this.heightInput) this.heightInput.disabled = disabled;
};

customElements.define('m-custom-size', CustomSize);
